/**
 * @typedef {'utp' | 'testinghub'} PlatformAnnouncementAudience
 * @typedef {'info' | 'warning' | 'error'} PlatformAnnouncementSeverity
 *
 * @typedef {Object} PlatformAnnouncement
 * @property {boolean} enabled
 * @property {PlatformAnnouncementSeverity} [severity]
 * @property {string} message
 * @property {string} [linkUrl]
 * @property {string} [linkLabel]
 * @property {string | null} [startsAt]
 * @property {string | null} [endsAt]
 * @property {PlatformAnnouncementAudience[]} [audience]
 */

const VALID_SEVERITIES = new Set(['info', 'warning', 'error']);
const VALID_AUDIENCE = new Set(['utp', 'testinghub']);

/**
 * @param {unknown} json
 * @returns {PlatformAnnouncement | null}
 */
export function parsePlatformAnnouncement(json) {
  if (json === null || typeof json !== 'object') {
    return null;
  }

  const record = /** @type {Record<string, unknown>} */ (json);

  if (typeof record.enabled !== 'boolean') {
    return null;
  }

  if (typeof record.message !== 'string') {
    return null;
  }

  const severity =
    typeof record.severity === 'string' && VALID_SEVERITIES.has(record.severity)
      ? record.severity
      : 'warning';

  let audience;
  if (Array.isArray(record.audience)) {
    audience = record.audience.filter(
      (item) => typeof item === 'string' && VALID_AUDIENCE.has(item),
    );
  }

  return {
    enabled: record.enabled,
    severity,
    message: record.message,
    linkUrl: typeof record.linkUrl === 'string' ? record.linkUrl : '',
    linkLabel: typeof record.linkLabel === 'string' ? record.linkLabel : 'Learn More',
    startsAt:
      record.startsAt === null || typeof record.startsAt === 'string' ? record.startsAt : null,
    endsAt: record.endsAt === null || typeof record.endsAt === 'string' ? record.endsAt : null,
    audience,
  };
}

/**
 * @param {Object} options
 * @param {PlatformAnnouncement} options.announcement
 * @param {PlatformAnnouncementAudience} options.product
 * @param {Date} [options.now]
 * @returns {boolean}
 */
export function isPlatformAnnouncementVisible({ announcement, product, now = new Date() }) {
  if (!announcement.enabled) {
    return false;
  }

  if (announcement.message.trim() === '') {
    return false;
  }

  const audience = announcement.audience;
  if (Array.isArray(audience) && audience.length > 0 && !audience.includes(product)) {
    return false;
  }

  if (announcement.startsAt) {
    const startsAt = Date.parse(announcement.startsAt);
    if (!Number.isNaN(startsAt) && now.getTime() < startsAt) {
      return false;
    }
  }

  if (announcement.endsAt) {
    const endsAt = Date.parse(announcement.endsAt);
    if (!Number.isNaN(endsAt) && now.getTime() >= endsAt) {
      return false;
    }
  }

  return true;
}
